import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fetchWrapper from '../utils/httpClient';
import {
  API_GET_PRODUCTS,
  API_GET_CODE_DICTIONARIES,
  API_GET_PACKAGE_PRODUCTS_BY_ID
} from '../utils/apis';

export const fetchPackageProductsDetail = createAsyncThunk(
  'managePackage/fetchPackageProductsDetail',
  async ({ packageId, region = 'SG', ...otherParams } = {}, { rejectWithValue }) => {
    try {
      const baseUrl = `/cms/api/packages/${packageId}/products`;
      const queryParams = new URLSearchParams({
        region,
        ...otherParams
      }).toString();

      const url = `${baseUrl}?${queryParams}`;
      const response = await fetchWrapper.get(url);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProducts = createAsyncThunk(
  'managePackage/fetchProducts',
  async ({ language, region = 'SG', ...otherParams } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        language,
        region,
        ...otherParams
      }).toString();

      const url = `${API_GET_PRODUCTS.split('?')[0]}?${queryParams}`;
      const response = await fetchWrapper.get(url);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCodeDictionaries = createAsyncThunk(
  'managePackage/fetchCodeDictionaries',
  async ({ region = 'SG', type = 'BUSINESS_DOMAIN', ...otherParams } = {}, { rejectWithValue }) => {
    try {
      const url = `${API_GET_CODE_DICTIONARIES}/${region}/${type}`;
      const response = await fetchWrapper.get(url);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLanguageOptions = createAsyncThunk(
  'managePackage/fetchLanguageOptions',
  async ({ region = 'ALL', type = 'LANGUAGE', ...otherParams } = {}, { rejectWithValue }) => {
    try {
      const url = `${API_GET_CODE_DICTIONARIES}/${region}/${type}`;
      const response = await fetchWrapper.get(url);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPackageProductsById = createAsyncThunk(
  'managePackage/fetchPackageProductsById',
  async ({ packageId, requestBody }, { rejectWithValue }) => {
    try {
      const url = `${API_GET_PACKAGE_PRODUCTS_BY_ID}/${packageId}/products`;

      const response = await fetchWrapper.post(url, requestBody);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const managePackageSlice = createSlice({
  name: 'managePackage',
  initialState: {
    products: [],
    codeDictionaries: [],
    packageProducts: [],
    packageProductsDetail: null,
    domainTypeProductMap: {},
    packageProductsDetailLoading: false,
    packageProductsDetailError: null,

    loading: false,
    error: null,
    currentLanguage: '',
    dictionaryLoading: false,
    dictionaryError: null,
    packageProductsLoading: false,
    packageProductsError: null,
    languageOptions: null,
    languageOptionsLoading: false,
    languageOptionsError: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.dictionaryError = null;
      state.packageProductsError = null;
      state.packageProductsDetailError = null; // 新增
    },
    setLanguage: (state, action) => {
      state.currentLanguage = action.payload;
    },
    clearDictionaryError: (state) => {
      state.dictionaryError = null;
    },
    clearPackageProductsError: (state) => {
      state.packageProductsError = null;
    },
    clearPackageProducts: (state) => {
      state.packageProducts = [];
    },
    clearPackageProductsDetail: (state) => {
      state.packageProductsDetail = null;
      state.domainTypeProductMap = {};
      state.packageProductsDetailError = null;
    },
    clearDomainTypeProductMap: (state) => {
      state.domainTypeProductMap = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchProducts
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchCodeDictionaries
      .addCase(fetchCodeDictionaries.pending, (state) => {
        state.dictionaryLoading = true;
        state.dictionaryError = null;
      })
      .addCase(fetchCodeDictionaries.fulfilled, (state, action) => {
        state.dictionaryLoading = false;
        state.codeDictionaries = action.payload;
      })
      .addCase(fetchCodeDictionaries.rejected, (state, action) => {
        state.dictionaryLoading = false;
        state.dictionaryError = action.payload;
      })
      // fetchPackageProductsById
      .addCase(fetchPackageProductsById.pending, (state) => {
        state.packageProductsLoading = true;
        state.packageProductsError = null;
      })
      .addCase(fetchPackageProductsById.fulfilled, (state, action) => {
        state.packageProductsLoading = false;
        state.packageProducts = action.payload;
      })
      .addCase(fetchPackageProductsById.rejected, (state, action) => {
        state.packageProductsLoading = false;
        state.packageProductsError = action.payload;
      })
      .addCase(fetchPackageProductsDetail.pending, (state) => {
        state.packageProductsDetailLoading = true;
        state.packageProductsDetailError = null;
      })
      .addCase(fetchPackageProductsDetail.fulfilled, (state, action) => {
        state.packageProductsDetailLoading = false;
        state.packageProductsDetail = action.payload;
        if (action.payload.resdata && action.payload.resdata.domainTypeProductMap) {
          state.domainTypeProductMap = action.payload.resdata.domainTypeProductMap;
        } else {
          state.domainTypeProductMap = {};
        }
      })
      .addCase(fetchPackageProductsDetail.rejected, (state, action) => {
        state.packageProductsDetailLoading = false;
        state.packageProductsDetailError = action.payload;
      })

      .addCase(fetchLanguageOptions.pending, (state) => {
        state.dictionaryLoading = true;
        state.dictionaryError = null;
      })
      .addCase(fetchLanguageOptions.fulfilled, (state, action) => {
        state.dictionaryLoading = false;
        state.languageOptions = action.payload;
      })
      .addCase(fetchLanguageOptions.rejected, (state, action) => {
        state.dictionaryLoading = false;
        state.dictionaryError = action.payload;
      });
  }
});

export const {
  clearError,
  setLanguage,
  clearDictionaryError,
  clearPackageProductsError,
  clearPackageProducts,
  clearPackageProductsDetail,
  clearDomainTypeProductMap
} = managePackageSlice.actions;

export default managePackageSlice.reducer;